import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Wrench, FileText, Bell } from 'lucide-react';
import useSeo from '../hooks/useSeo';
import FaqSection from '../components/FaqSection';

/**
 * SEO landing page targeting "low commission airbnb management uk" and
 * "self managed airbnb support uk". Static, content-rich, CTA to /become-host.
 * Per-page meta comes from the shared useSeo hook; crawlers see it via
 * the build-time prerender (frontend/scripts/prerender.js).
 */

const FAQ = [
  { question: "How much do Airbnb management companies charge in the UK?", answer: "Most full service Airbnb management companies in the UK take 15 to 30 percent of your booking revenue. Ariv is built for hosts who would rather keep that margin: it charges a 13 percent host commission, host only so there is no separate guest fee, which sits below Airbnb's own 15 to 18 percent. The first 100 founding hosts pay 0 percent for their first 90 days, then 10 percent for the rest of year one." },
  { question: "What is the cheapest way to manage an Airbnb without a full management company?", answer: "The cheapest approach for most UK hosts is to keep control of the listing yourself and buy in only the parts you cannot do alone, such as changeover cleaning and trades. That is exactly the model Ariv supports, so you avoid a large blanket commission while still getting local help as our network grows in your area." },
  { question: "Can I self manage my Airbnb and still get cleaning and maintenance help?", answer: "Yes. Self managing does not mean doing everything yourself, it means staying in control of your pricing, calendar and payouts while bringing in support around each booking. Ariv is designed so you can book a local changeover cleaner or a tradesperson when you need one, without signing up to a full management contract. We are pre launch and building that network city by city, and we will ID and background check cleaners and tradespeople as the network launches, so list your place and we will tell you where we stand in your area." },
  { question: "Do you have cleaners in my city yet?", answer: "We are honest about this: Ariv is pre launch and we are building our local cleaner and trade network city by city, so coverage is not everywhere yet. As the network launches we will ID and background check the cleaners and tradespeople on it. List your place at /become-host and we will tell you exactly where we stand in your area, and if you clean or work in the trades you can join the network at /provider/signup." },
  { question: "Is a low commission service worth it compared to a traditional Airbnb manager?", answer: "A traditional manager taking 15 to 30 percent is paying for a full hands off service, which suits hosts who never want to touch the listing. If you are happy to stay involved and just want reliable cleaning, trades and clear monthly statements, a low commission approach like Ariv keeps far more of your earnings in your pocket." },
];

const SERVICE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Low commission Airbnb management support",
  serviceType: "Short let management support for self managing hosts",
  provider: { "@type": "Organization", name: "Ariv", legalName: "Brick & Capital Ltd", url: "https://ariv.co.uk/" },
  areaServed: { "@type": "Country", name: "United Kingdom" },
  url: "https://ariv.co.uk/low-commission-airbnb-management/",
};
const JSONLD = [SERVICE_JSONLD];

const Section = ({ id, title, children }) => (
  <section id={id} className="max-w-3xl mx-auto px-6 py-8 border-t border-gray-100">
    <h2 className="text-2xl font-bold text-[#1F3A68] mb-3">{title}</h2>
    <div className="text-gray-700 leading-relaxed space-y-3">{children}</div>
  </section>
);

const LowCommissionManagementPage = () => {
  useSeo({
    title: "Low commission Airbnb management for UK hosts | Ariv",
    description: "Short stay support without the 15 to 30 percent management commission. Ariv gives UK hosts local cleaners, trades and clear monthly statements.",
    canonical: "https://ariv.co.uk/low-commission-airbnb-management/",
    image: "https://ariv.co.uk/icons/icon-512.png",
    imageAlt: "Ariv",
    faq: FAQ,
    jsonLd: JSONLD,
  });

  return (
    <div className="min-h-screen bg-white">
      <nav className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/"><img src="/logo.png" alt="Ariv" className="h-[30px] w-auto object-contain" /></Link>
          <Link to="/become-host" className="bg-[#1F3A68] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#1F3A68]/90">List your place</Link>
        </div>
      </nav>

      <header className="bg-gradient-to-br from-[#F4F2E9] to-white">
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <p className="inline-block text-xs font-semibold text-[#1F3A68] bg-[#1F3A68]/10 px-3 py-1 rounded-full mb-4">For self managing UK hosts</p>
          <h1 className="text-3xl md:text-4xl font-bold text-[#1F3A68] mb-4 leading-tight">
            Low commission Airbnb management for UK hosts
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed">
            Full service managers in the UK typically take 15 to 30 percent of your revenue. Ariv gives you the support that actually matters, local cleaners and trades, clear statements, and stock alerts, while you keep control and keep far more of what you earn.
          </p>
          <Link to="/become-host" className="inline-flex items-center gap-2 mt-6 bg-[#1F3A68] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#1F3A68]/90">
            Get set up this week <ArrowRight size={18} />
          </Link>
        </div>
      </header>

      <Section id="pay" title="What you pay, and what is included">
        <p>
          Traditional Airbnb management companies bundle everything into one large commission, often 15 to 30 percent of your booking revenue, and you have little visibility of where it goes. Ariv flips that. You pay a low host commission and get the practical support most managers charge a premium for.
        </p>
        <p>No long contracts, no exclusivity, and you keep direct control of your listing and your payouts.</p>
      </Section>

      <Section id="manage" title="What Ariv handles for you">
        <div className="grid sm:grid-cols-2 gap-4">
          {[
            { icon: Sparkles, t: 'Cleaning and changeovers', d: 'Line up a local changeover cleaner around each booking, without the last minute scramble.' },
            { icon: Wrench, t: 'Trades when something breaks', d: 'A trusted handyman or tradesperson on tap, so a broken boiler does not become a cancelled stay.' },
            { icon: FileText, t: 'Statements and admin', d: 'Clear monthly statements in plain English, so you always know what you earned and what was deducted.' },
            { icon: Bell, t: 'Stock and restock alerts', d: 'Live alerts when supplies run low at a property, so you restock before a guest notices.' },
          ].map(({ icon: Icon, t, d }) => (
            <div key={t} className="border border-gray-200 rounded-xl p-5">
              <Icon size={22} className="text-[#1F3A68] mb-2" />
              <p className="font-bold text-gray-900 mb-1">{t}</p>
              <p className="text-sm text-gray-600">{d}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section id="vetted" title="Cleaners and trades you can see, not random subcontractors">
        <p>
          The people who clean and fix your property matter as much as the booking itself. As the network launches we will ID and background check the cleaners and tradespeople on it, so you are not handing your keys to whoever was cheapest that week. You see who is coming and you stay in control.
        </p>
      </Section>

      <Section id="statement" title="Your monthly statement, explained">
        <p>
          At the end of each month you get a single clear statement: what you earned, what was deducted, and what is on its way to your account. No spreadsheet to build, no guessing. It is the kind of clarity full management promises and rarely delivers.
        </p>
      </Section>

      <Section id="pricing" title="How it compares to a typical UK manager">
        <div className="grid sm:grid-cols-2 gap-4 my-2">
          <div className="border border-gray-200 rounded-xl p-5">
            <p className="text-sm font-semibold text-gray-500 uppercase mb-1">Typical UK manager</p>
            <p className="text-3xl font-bold text-[#CB3B3B]">15 to 30%</p>
            <p className="text-sm text-gray-500 mt-1">of your booking revenue</p>
          </div>
          <div className="border-2 border-[#1F3A68] rounded-xl p-5 bg-[#1F3A68]/5">
            <p className="text-sm font-semibold text-[#1F3A68] uppercase mb-1">Ariv</p>
            <p className="text-3xl font-bold text-[#1F3A68]">0% for 90 days</p>
            <p className="text-sm text-gray-500 mt-1">as a founding host, then 10%, then 13% host only, plus the support you actually use</p>
          </div>
        </div>
        <div className="mt-6">
          <Link to="/become-host" className="inline-flex items-center gap-2 bg-[#1F3A68] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#1F3A68]/90">
            List your place on Ariv <ArrowRight size={18} />
          </Link>
        </div>
      </Section>

      <FaqSection faq={FAQ} />

      <footer className="bg-[#1F3A68] text-white py-8 mt-8">
        <div className="max-w-3xl mx-auto px-6 text-center text-white/70 text-sm">
          <p>&copy; {new Date().getFullYear()} Ariv. A product of Brick &amp; Capital Ltd.</p>
          <p className="text-xs mt-2 text-white/50">Registered in England and Wales, company number 16655788. Registered office: Bowness House, 815 Old Kent Road, London SE15 1NX.</p>
          <div className="flex flex-wrap gap-4 justify-center mt-3">
            <Link to="/become-host" className="hover:text-white">List your place</Link>
            <Link to="/airbnb-host-fee-alternative/" className="hover:text-white">Airbnb fee alternative</Link>
            <Link to="/airbnb-cleaning/" className="hover:text-white">Cleaning by city</Link>
            <Link to="/trust" className="hover:text-white">Trust &amp; Safety</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LowCommissionManagementPage;
